import { Box } from "@mui/material"
import { FilterAltOutlined } from "@mui-symbols-material/w300"
import { useParams } from "@tanstack/react-router"
import type { ChangeEvent, FC } from "react"
import { useMemo, useRef, useState } from "react"

import { useGetVendorApLedgerBalance } from "../../hooks/useGetVendorApLedgerBalance"
import { useGetVendorApLedgerEntries } from "../../hooks/useGetVendorApLedgerEntries"

import { AccountsPayableBalanceBanner } from "./AccountsPayableBalanceBanner"
import { AccountsPayableTransactionDocumentsDialog } from "./AccountsPayableTransactionDocumentsDialog"
import { AccountsPayableTransactionFilterDrawer } from "./AccountsPayableTransactionFilterDrawer"
import { AccountsPayableTransactionTable } from "./AccountsPayableTransactionTable"

import { Button } from "@/components/common/Button"
import SearchInput from "@/components/SearchInput/SearchInput"
import type { VendorApLedgerEntry, VendorApLedgerListFilter } from "@/graphql/codegen/graphql"
import { useScrollLoadMore } from "@/hooks/useScrollLoadMore"
import useDebounce from "@/utils/useDebounce"

const SEARCH_DEBOUNCE_MS = 300
const NOOP = () => {}

export const AccountsPayableBalanceTabPanel: FC = () => {
  const { companyId } = useParams({ from: "/app/companies/$companyId/edit" })

  const [searchInput, setSearchInput] = useState("")
  const [filter, setFilter] = useState<VendorApLedgerListFilter>({})
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [documentsEntry, setDocumentsEntry] = useState<VendorApLedgerEntry | null>(null)

  const debouncedQuery = useDebounce(searchInput, SEARCH_DEBOUNCE_MS)
  const hasActiveFilters = Boolean(filter.invoiceActions?.length || filter.datePostedFrom || filter.datePostedTo)
  const showBalanceColumn = !debouncedQuery && !hasActiveFilters

  const entriesFilter = useMemo<VendorApLedgerListFilter>(
    () => ({ ...filter, query: debouncedQuery || undefined }),
    [debouncedQuery, filter]
  )

  const { balance, isFetching: isBalanceFetching, error: balanceError } = useGetVendorApLedgerBalance({ companyId })
  const {
    entries,
    isFetching: isEntriesFetching,
    error: entriesError,
    hasMore,
    loadMore,
  } = useGetVendorApLedgerEntries({
    companyId,
    filter: entriesFilter,
  })

  const tableContainerRef = useRef<HTMLDivElement>(null)
  const handleScroll = useScrollLoadMore({
    fetching: isEntriesFetching,
    loadMore: hasMore ? loadMore : NOOP,
    scrollContainerRef: tableContainerRef,
  })

  const handleSearchInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchInput(event.target.value)
  }

  const handleFilterToggle = () => setIsFilterOpen((prev) => !prev)

  const handleFilterClear = () => setFilter({})

  const handleDocumentsClose = () => setDocumentsEntry(null)

  return (
    <Box className='flex min-h-0 flex-1 flex-col gap-4'>
      <AccountsPayableBalanceBanner balance={isBalanceFetching ? undefined : balance} error={balanceError} />
      <Box className='flex items-center gap-4'>
        <SearchInput
          placeholder='Search by Amount, Invoice #...'
          value={searchInput}
          onChange={handleSearchInputChange}
          className='w-full max-w-[524px]'
          data-testid='accounts-payable.search-input'
        />
        <Button
          appearance='outlined'
          startIcon={<FilterAltOutlined />}
          onClick={handleFilterToggle}
          data-testid='accounts-payable.filter-button'
        >
          Filter
        </Button>
      </Box>
      <AccountsPayableTransactionTable
        data={entries}
        loading={isEntriesFetching}
        error={entriesError}
        showBalanceColumn={showBalanceColumn}
        containerRef={tableContainerRef}
        onScroll={handleScroll}
        onDocumentsOpen={setDocumentsEntry}
      />
      <AccountsPayableTransactionFilterDrawer
        filter={filter}
        open={isFilterOpen}
        onToggle={handleFilterToggle}
        onChange={setFilter}
        onClear={handleFilterClear}
      />
      {documentsEntry && (
        <AccountsPayableTransactionDocumentsDialog entry={documentsEntry} onClose={handleDocumentsClose} />
      )}
    </Box>
  )
}
